import React from 'react';
import { Handshake, ExternalLink, Globe, Shield, TrendingUp, CheckCircle, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

// Real brand partners with corrected image paths
const brandPartners = [{
  id: 1,
  name: 'Aniq Studio Salon',
  logo: '/Client Logo/Aniq Salon.jpg',
  row: 1
}, {
  id: 2,
  name: 'Wapas Gadi',
  logo: '/Client Logo/Wapas Gadi.jpg',
  row: 1
}, {
  id: 3,
  name: 'Obtenir Solutions',
  logo: '/Client Logo/Obtenir Solutions.jpg',
  row: 2
}, {
  id: 4,
  name: 'M Store',
  logo: '/Client Logo/M Store.jpg',
  row: 2
}, {
  id: 5,
  name: 'Ramya Nagendra',
  logo: '/Client Logo/Ramya Nagendra.jpg',
  row: 2
}, {
  id: 6,
  name: 'InboxTales',
  logo: '/Client Logo/logo.png',
  row: 3
}, {
  id: 7,
  name: 'Sudesh Impact Group',
  logo: '/Client Logo/SIG Logo.png',
  row: 3
}, {
  id: 8,
  name: 'Flavors of Telugu',
  logo: '/Client Logo/FOT.png',
  row: 3
}];

// Partner benefits
const partnerBenefits = [{
  id: 1,
  title: "Global Reach",
  description: "Access to worldwide audiences through our extensive network",
  icon: Globe
}, {
  id: 2,
  title: "Brand Protection",
  description: "Maintain your brand integrity with our reputation management",
  icon: Shield
}, {
  id: 3,
  title: "Growth Opportunities",
  description: "Identify and capitalize on emerging market trends",
  icon: TrendingUp
}, {
  id: 4,
  title: "Quality Assurance",
  description: "Benefit from our rigorous quality standards",
  icon: CheckCircle
}];

// Shuffle function
const shuffleArray = <T,>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};


// Update the BrandLogo component (around line 90)
const BrandLogo = ({ brand }: { brand: typeof brandPartners[number] }) => (
  <div className="inline-flex flex-shrink-0 mx-6"> {/* Increased margin */}
    <div className="flex flex-col items-center justify-center w-[120px] md:w-[150px] hover:scale-110 transition-transform duration-300">
      {/* Increased width */}
      <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 w-20 h-20 flex items-center justify-center mb-3">
        {/* Increased container size */}
        <img
          src={brand.logo}
          alt={`${brand.name} logo`}
          className="h-12 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
          // {/* Increased logo size */}
          onError={e => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = '/placeholder.svg';
          }}
        />
      </div>
      <p className="text-sm font-medium text-center text-gray-600 font-montserrat line-clamp-1">
        {/* Slightly larger text */}
        {brand.name}
      </p>
    </div>
  </div>
);


const HandshakedBrandsSection: React.FC = () => {
  const isMobile = useIsMobile();


  // Filter brands by row
  const row1Brands = brandPartners.filter(brand => brand.row === 1);
  const row2Brands = brandPartners.filter(brand => brand.row === 2);
  const row3Brands = brandPartners.filter(brand => brand.row === 3);

  // Inside the component, before return statement
  const deduplicate = (arr: typeof brandPartners) => {
    const seen = new Set();
    return arr.filter(brand => {
      if (seen.has(brand.id)) return false;
      seen.add(brand.id);
      return true;
    });
  };

  const allBrands = deduplicate([...row1Brands, ...row2Brands, ...row3Brands]);


  return <section className="py-20 md:py-28 bg-gradient-to-b from-background via-secondary/30 to-background border-y border-border/20 relative overflow-hidden">
    {/* Decorative elements */}
    <div className="absolute top-0 left-0 w-full h-full bg-grid-pattern opacity-5"></div>
    <div className="absolute top-0 right-0 w-64 h-64 bg-purple-200/30 rounded-full blur-3xl"></div>
    <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl"></div>

    <div className="container mx-auto px-4 md:px-6 relative z-10">
      <div className="max-w-4xl mx-auto">
        {/* Section header */}
        <div className="flex flex-col items-center justify-center mb-12 md:mb-16 text-center">
          <div className="bg-gradient-to-br from-primary/20 to-purple-200/30 p-4 rounded-full mb-6 shadow-lg">
            <Handshake className="h-10 w-10 text-primary" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 font-playfair bg-clip-text text-transparent bg-gradient-to-r from-primary to-purple-700">
            Our Trusted Partners
          </h2>
          <p className="mt-2 text-base md:text-lg text-muted-foreground max-w-2xl font-poppins">
            Collaborating with industry leaders to deliver exceptional results and drive innovation
          </p>
        </div>

        {/* Partner benefits - Redesigned with clearer visuals */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-16">
          {partnerBenefits.map(benefit => {
            const IconComponent = benefit.icon;
            return <div key={benefit.id} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-purple-50 hover:border-purple-200 group">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <IconComponent className="h-6 w-6 text-purple-700" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2 font-montserrat group-hover:text-purple-700 transition-colors duration-300">{benefit.title}</h3>
                  <p className="text-muted-foreground text-sm font-poppins">{benefit.description}</p>
                </div>
              </div>
            </div>;
          })}
        </div>



        {/* Fix 3: Single logo section with proper animation */}

        <div className="bg-white py-12 px-6 rounded-3xl shadow-lg border border-gray-100 mb-16">
          <h3 className="text-center text-2xl md:text-3xl font-playfair font-medium mb-10 text-gray-800">
            Trusted by Leading Brands Worldwide
          </h3>

          <div className="w-full overflow-hidden relative h-[180px]">
            <div className="flex animate-scroll-rtl whitespace-nowrap items-center h-full">
              {[...allBrands, ...allBrands].map((brand, index) => (
                <BrandLogo key={`${brand.id}-${index}`} brand={brand} />
              ))}
            </div>
          </div>
        </div>


        {/* CTA section - Updated to community link */}
        <div className="mt-14 md:mt-16 bg-gradient-to-r from-purple-600 to-purple-800 rounded-xl p-8 shadow-lg text-center text-primary-foreground relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full"></div>
          <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/5 rounded-full"></div>

          <MessageSquare className="inline-block h-12 w-12 mb-4 text-amber-200" />
          <p className="text-xl md:text-2xl font-bold mb-4 md:mb-6 max-w-2xl mx-auto font-playfair">
            Join Our Community
          </p>
          <p className="text-white/80 mb-6 max-w-md mx-auto font-poppins">
            Be a part of our growing network! Share ideas, learn from experts, and stay updated with the latest trends and insights.
          </p>

          <Button size={isMobile ? "default" : "lg"} variant="outline" className="bg-white text-purple-700 hover:bg-white/90 border-white group" onClick={() => window.open("https://whatsapp.com/channel/0029Vak8HQZGU3BRAbCQQg3e", "_blank")}>
            <span className="font-montserrat font-medium">Join WhatsApp Community</span>
            <ExternalLink className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </div>
  </section>;
};
export default HandshakedBrandsSection;
